import { defineStore } from "pinia";
import { computed, ref } from "vue";

export type TSnackDialogType = "success" | "error" | "info" | "sad";

export interface ISnackDialogPayload {
  title: string;
  text?: string;
  type?: TSnackDialogType;
  confirmText?: string;
  onConfirm?: () => void | Promise<void>;
}

export const useSnackDialogStore = defineStore("snackDialog", () => {
  const open = ref(false);
  const loading = ref(false);

  const title = ref("");
  const text = ref("");
  const type = ref<TSnackDialogType>("success");
  const confirmText = ref("OK");
  const onConfirm = ref<ISnackDialogPayload["onConfirm"]>();
  const hasAction = computed(() => Boolean(onConfirm.value));

  function show(payload: ISnackDialogPayload) {
    title.value = payload.title;
    text.value = payload.text ?? "";
    type.value = payload.type ?? "success";
    confirmText.value = payload.confirmText ?? "OK";
    onConfirm.value = payload.onConfirm;
    open.value = true;
  }

  function showSuccess(
    title: string,
    text = "",
    confirmText = "OK",
    onConfirm?: () => void | Promise<void>
  ) {
    show({
      title,
      text,
      type: "success",
      confirmText,
      onConfirm,
    });
  }

  function showError(
    title: string,
    text = "",
    confirmText = "OK",
    onConfirm?: () => void | Promise<void>
  ) {
    show({
      title,
      text,
      type: "error",
      confirmText,
      onConfirm,
    });
  }

  function close() {
    open.value = false;
    loading.value = false;
    onConfirm.value = undefined;
  }

  async function confirm() {
    try {
      loading.value = true;
      await onConfirm.value?.();
      close();
    } catch (error) {
      loading.value = false;
      throw error;
    }
  }

  return {
    open,
    loading,
    hasAction,
    title,
    text,
    type,
    confirmText,
    show,
    showSuccess,
    showError,
    close,
    confirm,
  };
});
